import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CardModule } from 'primeng/card';
import { AsyncPipe, NgIf } from '@angular/common';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Offer } from '@app/resources/services';
import { LoyaltyProgramsTypesEnum, OfferStatusTypes } from '@shared/enums';
import {
  OfferFormattedDatePipe,
  OfferFormattedReminderPipe,
  OfferStatusPipe,
  OfferSmsBodyPipe,
  OfferStatusSeverityPipe,
} from '@app/areas/offers/pipes';
import { OfferDetailsHeaderButtonsComponent } from '@app/areas/offers/components/offer-details-header-buttons/offer-details-header-buttons.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { WindowService } from '@app/resources/services/window.service';
import { TooltipModule } from 'primeng/tooltip';
import { ApplicationProfileService } from '@shared/services/application-profile.service';
import { map } from 'rxjs/operators';
import { ApplicationProfile } from '@app/@shared/models';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';

@Component({
  selector: 'app-offer-details-card',
  templateUrl: './offer-details-card.component.html',
  styleUrls: ['./offer-details-card.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    CardModule,
    AsyncPipe,
    DividerModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    OfferFormattedDatePipe,
    OfferFormattedReminderPipe,
    OfferStatusSeverityPipe,
    OfferDetailsHeaderButtonsComponent,
    OverlayPanelModule,
    OfferStatusPipe,
    OfferSmsBodyPipe,
  ],
})
export class OfferDetailsCardComponent {
  private readonly windowService = inject(WindowService);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);

  @Input() offer: Offer | null = null;
  @Output() duplicateOfferEvent = new EventEmitter(false);
  @Output() editOfferEvent = new EventEmitter(false);

  applicationProfile!: ApplicationProfile;

  get isSmallScreen() {
    return this.windowService.isSmallScreen;
  }

  get isPendingTextNotSent() {
    return this.offer?.statusType === OfferStatusTypes.PendingNoText;
  }

  protected getOfferDiscountAmount$() {
    return this.applicationProfileService.applicationProfile$.pipe(
      map((appProfile) => {
        let newDiscount = this.offer?.receiveAmount ?? 0;
        this.applicationProfile = appProfile;
        this.setCSSCustomProperties();
        if (
          (appProfile.Merchant.LoyaltyProgram.LoyaltyProgramType & LoyaltyProgramsTypesEnum.CashBack) ===
          LoyaltyProgramsTypesEnum.CashBack
        ) {
          newDiscount += appProfile.Merchant.LoyaltyProgram.ReceiveAmount * 10000;
        }
        return `Up to ${newDiscount}% off`;
      })
    );
  }

  protected duplicateOffer() {
    this.duplicateOfferEvent.emit(true);
  }

  protected editOffer() {
    this.editOfferEvent.emit(true);
  }

  private setCSSCustomProperties(): void {
    if (this.applicationProfile?.PortalTheme?.Theme) {
      const theme = this.applicationProfile.PortalTheme.Theme;
      // Set CSS custom properties
      document.documentElement.style.setProperty('--primary', theme.primary);
      document.documentElement.style.setProperty('--form-header-bg-primary', theme.form.header?.primaryBackgroundColor);
      document.documentElement.style.setProperty('--form-header-text-color', theme.form?.header?.textColor);
      document.documentElement.style.setProperty('--form-bg-color', theme.form?.backgroundColor);
      document.documentElement.style.setProperty('--text--color-default', theme.defaultTextColor);
      document.documentElement.style.setProperty('--form-header-items-color', theme.form.header.itemsColor);
      document.documentElement.style.setProperty('--form-header-items-text-color', theme.form.header.itemsTextColor);

      this.cssValidatorService.checkUndefinedCssVariables('OfferDetailsCardComponent');
    }
  }
}
